import React from "react";
import { useTranslation } from "react-i18next";
import "./TelealarmeISSTechFAQ.css";

/**
 * Téléalarme ISS — Section 07 / Informations techniques & FAQ
 * Composant autonome : textes FR/EN locaux, sans changement du i18n global.
 * Les caractéristiques exactes restent à confirmer selon le matériel retenu.
 * Les questions emploient <details> / <summary> pour fonctionner au clavier,
 * même sans JavaScript côté navigateur après rendu HTML.
 */
const COPY = {
  fr: {
    eyebrow: "L'ESSENTIEL POUR DÉCIDER",
    title: "Toutes les informations pour préparer votre installation.",
    intro: "Une lecture immédiate pour le décideur, avec un accès au détail pour l'équipe technique.",
    cardTitle: "Caractéristiques à préciser",
    characteristics: [
      { label: "Communication", value: "4G / IP — selon version" },
      { label: "Installation", value: "Interfaces à qualifier" },
      { label: "Alimentation", value: "Options de secours à préciser" },
      { label: "Supervision", value: "Selon configuration retenue" }
    ],
    docAction: "Demander la documentation",
    technicalNote:
      "Les paramètres électriques, l'autonomie, les normes et les seuils d'alerte seront précisés après validation de la documentation constructeur.",
    faqLabel: "Questions fréquentes sur la téléalarme ISS",
    faqs: [
      {
        question: "La solution convient-elle à mon ascenseur ?",
        answer:
          "La compatibilité dépend des interfaces et de l'équipement existant. Une étude de votre installation permet de déterminer la configuration adaptée."
      },
      {
        question: "Que se passe-t-il en cas de coupure de réseau ?",
        answer:
          "La communication dépend du réseau disponible et des dispositifs de secours prévus pour l'installation. La couverture, les conditions de fonctionnement et les essais à réaliser doivent être vérifiés lors de l'étude technique."
      },
      {
        question: "Peut-on consulter les événements à distance ?",
        answer:
          "Certaines configurations permettent de consulter des informations ou des événements à distance. Les données accessibles dépendent des équipements, des options activées et des droits d'accès retenus."
      },
      {
        question: "Quels documents sont disponibles ?",
        answer:
          "Les documents applicables sont à confirmer selon la référence et la version du matériel : fiches techniques, notices d'installation et, le cas échéant, justificatifs réglementaires. Contactez notre équipe pour obtenir la documentation correspondant à votre projet."
      }
    ]
  },
  en: {
    eyebrow: "KEY INFORMATION FOR YOUR DECISION",
    title: "Everything you need to plan your installation.",
    intro: "An immediate overview for decision-makers, with technical detail available to your engineering team.",
    cardTitle: "Specifications to confirm",
    characteristics: [
      { label: "Communication", value: "4G / IP — depending on version" },
      { label: "Installation", value: "Interfaces to be assessed" },
      { label: "Power supply", value: "Backup arrangements to confirm" },
      { label: "Monitoring", value: "Depending on configuration" }
    ],
    docAction: "Request documentation",
    technicalNote:
      "Electrical ratings, backup autonomy, standards and alert thresholds will be specified after checking the manufacturer's documentation.",
    faqLabel: "Frequently asked questions about ISS emergency calling",
    faqs: [
      {
        question: "Is the solution suitable for my elevator?",
        answer:
          "Compatibility depends on the available interfaces and existing equipment. Reviewing your installation helps determine the appropriate configuration."
      },
      {
        question: "What happens if the network goes down?",
        answer:
          "Communication depends on the available network and the backup arrangements designed for the installation. Coverage, operating conditions and required tests must be reviewed during the technical assessment."
      },
      {
        question: "Can I view events remotely?",
        answer:
          "Some configurations support remote access to information or events. Available data depends on the equipment, enabled options and access permissions."
      },
      {
        question: "Which documents are available?",
        answer:
          "Applicable documents need to be confirmed against the equipment reference and version, including technical sheets, installation manuals and, where applicable, regulatory evidence. Contact our team for the documents relevant to your project."
      }
    ]
  }
};

export default function TelealarmeISSTechFAQ() {
  const { i18n } = useTranslation();
  const language = (i18n.resolvedLanguage || i18n.language || "fr")
    .split("-")[0]
    .toLowerCase();
  const content = COPY[language] || COPY.fr;

  return (
    <section
      id="iss-technical-faq"
      className="iss-techfaq"
      aria-labelledby="iss-techfaq-title"
    >
      <div className="iss-techfaq__container">
        <header className="iss-techfaq__header">
          <p className="iss-techfaq__eyebrow">{content.eyebrow}</p>
          <h2 className="iss-techfaq__title" id="iss-techfaq-title">
            {content.title}
          </h2>
          <p className="iss-techfaq__intro">{content.intro}</p>
        </header>

        <div className="iss-techfaq__columns">
          <div className="iss-techfaq__specCard">
            <h3 className="iss-techfaq__specHeading">{content.cardTitle}</h3>

            <dl className="iss-techfaq__specList">
              {content.characteristics.map((characteristic) => (
                <div className="iss-techfaq__specRow" key={characteristic.label}>
                  <dt>{characteristic.label}</dt>
                  <dd>{characteristic.value}</dd>
                </div>
              ))}
            </dl>

            <a className="iss-techfaq__docLink" href="/contact">
              <span>{content.docAction}</span>
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true" focusable="false">
                <path d="M4 12h15m-6-6 6 6-6 6" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>

            <p className="iss-techfaq__technicalNote">{content.technicalNote}</p>
          </div>

          <div className="iss-techfaq__faqCard" aria-label={content.faqLabel}>
            {content.faqs.map((item, index) => (
              <details
                className="iss-techfaq__item"
                key={`${language}-${index}`}
                open={index === 0}
              >
                <summary className="iss-techfaq__question">
                  <span>{item.question}</span>
                  <span className="iss-techfaq__indicator" aria-hidden="true" />
                </summary>
                <div className="iss-techfaq__answer">
                  <p>{item.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
